import { useEffect, useMemo, useState } from 'react'
import { Crown } from 'lucide-react'
import { Modal } from './modal'

export type DomainCaptchaModalProps = {
  open: boolean
  /** 验证码内容：域名 或 "绅士网盘" */
  content: string
  /** 顺序点选片段（后端拼音分词 / 兜底固定两段），拼接应等于 content。 */
  segments: string[]
  onPass: () => void
  onCancel: () => void
  /** 点「购买VIP免验证码」→ 跳 VIP 购买页(由调用方注入 navigate,shared 组件保持路由无关)。 */
  onBuyVip: () => void
}

type Tile = { chunk: string; origIdx: number }

// shuffle 返回新数组（Fisher-Yates）。展示顺序打乱，tile 仍带原始下标，校验按下标顺序。
function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

/**
 * DomainCaptchaModal — 域名字序点选验证码（片段由后端分词 / gate 兜底提供）。
 * 顶部显示完整 content（目标顺序，逼用户读完防墙域名）；下方打乱的片 tile，
 * 按 content 顺序依次点击；点对进位、点错抖动重置；点完即 onPass。
 */
export function DomainCaptchaModal({ open, content, segments, onPass, onCancel, onBuyVip }: DomainCaptchaModalProps) {
  const chunks = segments
  const tiles = useMemo<Tile[]>(
    () => shuffle(chunks.map((chunk, origIdx) => ({ chunk, origIdx }))),
    // 仅在 content 变化时重排（避免每次 render 重洗）
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content],
  )
  const [clicked, setClicked] = useState<number[]>([])
  const [shake, setShake] = useState(false)

  useEffect(() => {
    setClicked([])
    setShake(false)
  }, [content, open])

  if (!open) return null

  const onTile = (origIdx: number) => {
    const expected = clicked.length // 正确顺序就是原始下标 0,1,2,...
    // 文字与期望片段相同即算对(域名可能切出相同文字片,如 nini.com 两个 ni;按下标严判会误伤)
    if (chunks[origIdx] === chunks[expected] && !clicked.includes(origIdx)) {
      const next = [...clicked, origIdx]
      setClicked(next)
      if (next.length === chunks.length) onPass()
    } else {
      setShake(true)
      setClicked([])
      window.setTimeout(() => setShake(false), 400)
    }
  }

  return (
    // onClose 设为 no-op:gate 不可被点击遮罩空白处 / Esc 绕过(否则点框外即 navigate 回列表)。
    // 只有两条出路 —— 按序点片解开(onPass)或显式点「取消」按钮(onCancel)。
    <Modal open={open} onClose={() => {}} data-testid="domcap-modal">
      <div className="flex flex-col gap-4 p-1">
        <div className="text-center">
          <p className="text-[13px] text-[#9CA3AF]">请按顺序点击下方片段，记住本站防墙域名</p>
          <p className="mt-1 break-all text-[18px] font-bold tracking-wide text-[#1A1A1A]" data-testid="domcap-target">
            {content}
          </p>
        </div>

        {/* 已选进度 */}
        <div className="min-h-[28px] text-center text-[15px] font-semibold text-[#19caad]">
          {clicked.map((i) => chunks[i]).join('')}
        </div>

        {/* 打乱的可点 tile */}
        <div className={`flex flex-wrap justify-center gap-2 ${shake ? 'animate-pulse' : ''}`}>
          {tiles.map(({ chunk, origIdx }) => {
            const done = clicked.includes(origIdx)
            return (
              <button
                key={origIdx}
                type="button"
                data-testid={`domcap-tile-${origIdx}`}
                disabled={done}
                onClick={() => onTile(origIdx)}
                className={`rounded-[10px] border px-3 py-2 text-[16px] font-semibold ${
                  done
                    ? 'border-[#19caad] bg-[#F0FBF9] text-[#19caad] opacity-60'
                    : 'border-[#E5E7EB] bg-white text-[#1A1A1A] active:bg-[#F6F7F8]'
                }`}
              >
                {chunk}
              </button>
            )
          })}
        </div>

        {/* 底部双按钮:取消(左,弱)+ 购买VIP免验证码(右,显眼)。 */}
        <div className="mt-1 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="domcap-cancel"
            onClick={onCancel}
            className="shrink-0 text-[13px] text-[#9CA3AF] underline"
          >
            取消
          </button>
          {/* 配色对齐 /#/pack 订阅 CTA(share-pack 立即订阅):近黑底 + 金王冠 + 白字。 */}
          <button
            type="button"
            data-testid="domcap-buy-vip"
            onClick={onBuyVip}
            className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-[#1A1A1A] px-4 text-[13px] font-semibold text-white active:bg-black"
          >
            <Crown size={15} className="text-[#F59E0B]" aria-hidden="true" />
            购买VIP免验证码
          </button>
        </div>
      </div>
    </Modal>
  )
}
